import { memo, useCallback, useEffect, useRef, useState } from 'react'
import { css, jsx } from '@emotion/react'
import Masonry, { ResponsiveMasonry } from 'react-responsive-masonry'
import NProgress from 'nprogress'
import copy from 'copy-to-clipboard'
import { toastError } from '../../constants'
import BookmarkListItem from '../dashboard/rightContent/BookmarkListItem'
import BookmarkCardItem from '../dashboard/rightContent/BookmarkCardItem'
import LoadingFirstTimeBlocks from '../dashboard/rightContent/LoadingFirstTimeBlocks'
import LoadingFirstTimeCards from '../dashboard/rightContent/LoadingFirstTimeCards'
import { Text } from '../chakra/Text'
import { MenuItem, MenuList } from '../chakra/Menu'
import { getPublicBookmarksFolderFetcher } from '../../services/public'
import Emoji from '../Emoji'
import NoDataSvgLogo from '../dashboard/rightContent/NoDataSvgLogo'
import { Toast } from '../chakra/Toast'
import { ClipboardCopied } from '../chakra/icons/ClipboardCopied'

const styles = {
  menuContent: css`
    min-width: 8rem;
  `,
  menuItem: css`
    font-size: 14px;
    padding-top: 0.3rem;
    padding-bottom: 0.3rem;
  `,
}

const getBookmarks = async (
  setBookmarks,
  options,
  setInternalLoading,
  currentTokenRef,
  tokenRef,
  userId,
  folderId,
  setError
) => {
  try {
    NProgress.start()
    setError(false)
    setInternalLoading(true)
    const list = await getPublicBookmarksFolderFetcher(`/public/bookmarks/${userId}/${folderId}`, options)
    if (currentTokenRef !== tokenRef.current) {
      return
    }
    setBookmarks(list)
    setInternalLoading(false)
    NProgress.done()
  } catch (e) {
    toastError('bookmarks')
    setInternalLoading(false)
    setError(true)
    NProgress.done()
  }
}

const columnsCountBreakPoints = { 425: 1, 700: 2, 1150: 3 }

function BookmarkListContainer({
  options,
  padding,
  isSearching,
  bookmarks: internalBookmarks,
  userId,
  folderId,
  bookmarksViewMode,
  isErrorBookmarks,
}) {
  const tokenRef = useRef()
  const [bookmarks, setBookmarks] = useState(null)
  const [internalLoading, setInternalLoading] = useState(true)
  const ListItemComp = bookmarksViewMode?.viewLikeCard ? BookmarkCardItem : BookmarkListItem
  const loadingFirstTime = !internalBookmarks
  const [error, setError] = useState(false)

  const ListContainer = useCallback(
    ({ children }) =>
      bookmarksViewMode?.viewLikeCard ? (
        <ResponsiveMasonry columnsCountBreakPoints={columnsCountBreakPoints}>
          <Masonry columnsCount={3} gutter={padding}>
            {children}
          </Masonry>
        </ResponsiveMasonry>
      ) : (
        <>{children}</>
      ),
    [bookmarksViewMode?.viewLikeCard, padding]
  )

  const menuList = useCallback(
    bookmark => (
      <MenuList css={styles.menuContent}>
        <MenuItem
          css={styles.menuItem}
          onClick={e => {
            e.stopPropagation()
            copy(bookmark.url)
            Toast({
              title: 'Bookmark URL copied to clipboard!',
              description: bookmark.url,
              icon: <ClipboardCopied boxSize={4} />,
              duration: 20000,
              isClosable: true,
              position: 'top-right',
            })
          }}
        >
          Copy URL
        </MenuItem>
        <MenuItem
          css={styles.menuItem}
          onClick={e => {
            e.stopPropagation()
            window.open(`https://web.archive.org/web/changes/${bookmark.url}`, '_blank')
          }}
        >
          Open Web archive
        </MenuItem>
      </MenuList>
    ),
    []
  )

  useEffect(() => {
    if (userId && folderId) {
      // To cancel request if we fired a next one
      tokenRef.current = Math.random()
      getBookmarks(setBookmarks, options, setInternalLoading, tokenRef.current, tokenRef, userId, folderId, setError)
    }
  }, [options, internalBookmarks, userId, folderId])

  if (isErrorBookmarks || error) {
    return (
      <Text fontSize="15px" block textAlign={['center', 'center', 'center', 'left']}>
        Error fetching bookmarks <Emoji>🤦🏻‍♂️</Emoji>
      </Text>
    )
  }

  if (loadingFirstTime) {
    if (!bookmarksViewMode) return null

    return bookmarksViewMode?.viewLikeCard ? (
      <LoadingFirstTimeCards ListContainer={ListContainer} />
    ) : (
      <LoadingFirstTimeBlocks />
    )
  }

  if (internalLoading) {
    return null
  }

  if (bookmarks?.length === 0) {
    return isSearching ? (
      <Text fontSize="15px" block textAlign={['center', 'center', 'center', 'left']}>
        There are no bookmarks that match the search <Emoji>🤷‍♂️️</Emoji>
      </Text>
    ) : (
      <NoDataSvgLogo text="No bookmarks saved yet" />
    )
  }

  return (
    <ListContainer>
      {bookmarks?.map((bookmark, i) => (
        <ListItemComp
          key={bookmark._id}
          bookmark={bookmark}
          viewMode={bookmarksViewMode}
          isLastItem={i === bookmarks.length - 1}
          menuList={menuList}
          isPublicMode
        />
      ))}
    </ListContainer>
  )
}

export default memo(BookmarkListContainer)
